import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Bar, Line, Radar } from "react-chartjs-2";
import article from "./StopLossContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement,
  RadialLinearScale
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement,
  RadialLinearScale
);

const RiskExposureMeterCalculator = () => {
  const content = article.find((item) => item.id === 9);
  const [activeTab, setActiveTab] = useState("meter");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [analysisMode, setAnalysisMode] = useState("comprehensive");
  const [notifications, setNotifications] = useState([]);
  const [calculationHistory, setCalculationHistory] = useState([]);

  const [inputs, setInputs] = useState({
    // Portfolio Overview
    totalPortfolioValue: "100000",
    liquidAssets: "25000",
    monthlyIncome: "5000",
    monthlyExpenses: "3500",
    emergencyFund: "15000",
    
    // Current Positions
    stockPositions: "45000",
    optionPositions: "5000",
    cryptoPositions: "5000",
    otherPositions: "5000",
    cashPosition: "30000",
    
    // Risk Exposure
    leveragedPositions: "0",
    marginUsed: "0",
    portfolioBeta: "1.2",
    concentrationRisk: "15",
    
    // Market Conditions
    marketVolatility: "Normal",
    vixLevel: "18.5",
    economicOutlook: "Stable",
    sectorCorrelation: "0.7",
    
    // Risk Parameters
    maxDrawdownTolerance: "15",
    maxLeverage: "2",
    riskAppetite: "moderate",
    timeHorizon: "5",
    
    // Stress Test Scenarios
    marketCrashScenario: "20",
    volatilitySpike: "50",
    blackSwanEvent: "35",
    correlationBreakdown: "0.9"
  });

  const [exposureAnalysis, setExposureAnalysis] = useState(null);
  const [stressTestResults, setStressTestResults] = useState(null);
  const chartRef = useRef();

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "meter" || activeTab === "stress") {
      calculateRiskExposure();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const calculateRiskExposure = () => {
    const {
      totalPortfolioValue,
      liquidAssets,
      monthlyIncome,
      monthlyExpenses,
      emergencyFund,
      stockPositions,
      optionPositions,
      cryptoPositions,
      otherPositions,
      cashPosition,
      leveragedPositions,
      marginUsed,
      portfolioBeta,
      concentrationRisk,
      marketVolatility,
      vixLevel,
      economicOutlook,
      sectorCorrelation,
      maxDrawdownTolerance,
      maxLeverage,
      riskAppetite,
      timeHorizon,
      marketCrashScenario,
      volatilitySpike,
      blackSwanEvent,
      correlationBreakdown
    } = inputs;

    if (!totalPortfolioValue) {
      return;
    }

    const portfolioValue = parseFloat(totalPortfolioValue);
    const liquid = parseFloat(liquidAssets) || 0;
    const monthlyInc = parseFloat(monthlyIncome) || 0;
    const monthlyExp = parseFloat(monthlyExpenses) || 0;
    const emergency = parseFloat(emergencyFund) || 0;
    
    const stocks = parseFloat(stockPositions) || 0;
    const options = parseFloat(optionPositions) || 0;
    const crypto = parseFloat(cryptoPositions) || 0;
    const other = parseFloat(otherPositions) || 0;
    const cash = parseFloat(cashPosition) || 0;
    
    const leveraged = parseFloat(leveragedPositions) || 0;
    const margin = parseFloat(marginUsed) || 0;
    const beta = parseFloat(portfolioBeta) || 1.0;
    const concentration = parseFloat(concentrationRisk) || 0;
    
    const maxDD = parseFloat(maxDrawdownTolerance) / 100;
    const maxLev = parseFloat(maxLeverage) || 1;
    const timeHoriz = parseInt(timeHorizon) || 5;
    
    const marketCrash = parseFloat(marketCrashScenario) / 100;
    const volSpike = parseFloat(volatilitySpike) / 100;
    const blackSwan = parseFloat(blackSwanEvent) / 100;
    const corrBreakdown = parseFloat(correlationBreakdown) || 0.9;

    // Calculate portfolio composition
    const totalInvested = stocks + options + crypto + other;
    const cashPercentage = (cash / portfolioValue) * 100;
    const investedPercentage = (totalInvested / portfolioValue) * 100;
    const leveragedPercentage = (leveraged / portfolioValue) * 100;

    // Calculate risk scores (0-100)
    const liquidityScore = calculateLiquidityScore(liquid, monthlyExp, emergency);
    const concentrationScore = calculateConcentrationScore(concentration, stocks, options, crypto);
    const leverageScore = calculateLeverageScore(leveraged, margin, portfolioValue, maxLev);
    const marketScore = calculateMarketScore(beta, marketVolatility, vixLevel);
    const diversificationScore = calculateDiversificationScore(stocks, options, crypto, other, cash);
    const timeHorizonScore = calculateTimeHorizonScore(timeHoriz, riskAppetite);

    // Overall risk exposure score
    const overallExposureScore = (
      liquidityScore * 0.2 +
      concentrationScore * 0.25 +
      leverageScore * 0.25 +
      marketScore * 0.15 +
      diversificationScore * 0.10 +
      timeHorizonScore * 0.05
    );

    // Risk capacity calculations
    const riskCapacity = calculateRiskCapacity(
      portfolioValue,
      monthlyInc,
      monthlyExp,
      emergency,
      timeHoriz
    );

    // Stress test scenarios
    const stressTests = calculateStressTests(
      portfolioValue,
      stocks,
      options,
      crypto,
      other,
      beta,
      marketCrash,
      volSpike,
      blackSwan,
      corrBreakdown
    );

    // Risk exposure levels
    const exposureLevel = getExposureLevel(overallExposureScore);
    const riskAppetiteMatch = assessRiskAppetiteMatch(overallExposureScore, riskAppetite);

    const analysis = {
      portfolio: {
        totalValue: portfolioValue,
        investedValue: totalInvested,
        cashValue: cash,
        cashPercentage,
        investedPercentage,
        leveragedPercentage
      },
      composition: {
        stocks: (stocks / portfolioValue) * 100,
        options: (options / portfolioValue) * 100,
        crypto: (crypto / portfolioValue) * 100,
        other: (other / portfolioValue) * 100,
        cash: cashPercentage
      },
      riskScores: {
        liquidity: liquidityScore,
        concentration: concentrationScore,
        leverage: leverageScore,
        market: marketScore,
        diversification: diversificationScore,
        timeHorizon: timeHorizonScore,
        overall: overallExposureScore
      },
      capacity: riskCapacity,
      stressTests,
      exposure: {
        level: exposureLevel.level,
        color: exposureLevel.color,
        description: exposureLevel.description,
        recommendation: exposureLevel.recommendation
      },
      assessment: riskAppetiteMatch,
      metrics: {
        beta,
        concentration,
        maxDrawdownTolerance: maxDD * 100,
        monthlySavings: monthlyInc - monthlyExp,
        emergencyMonths: emergency / monthlyExp
      }
    };

    setExposureAnalysis(analysis);
    calculateDetailedStressTests(analysis, stressTests);
  };

  const calculateLiquidityScore = (liquidAssets, monthlyExpenses, emergencyFund) => {
    let score = 50;
    
    // Emergency fund coverage
    const monthsCovered = emergencyFund / monthlyExpenses;
    if (monthsCovered >= 6) score += 30;
    else if (monthsCovered >= 3) score += 15;
    else if (monthsCovered < 1) score -= 20;
    
    // Liquid assets ratio
    const liquidRatio = liquidAssets / monthlyExpenses;
    if (liquidRatio >= 12) score += 20;
    else if (liquidRatio >= 6) score += 10;
    else if (liquidRatio < 3) score -= 15;
    
    return Math.max(0, Math.min(100, score));
  };

  const calculateConcentrationScore = (concentrationRisk, stocks, options, crypto) => {
    let score = 50;
    
    // Concentration risk from input
    if (concentrationRisk <= 10) score += 25;
    else if (concentrationRisk <= 20) score += 10;
    else if (concentrationRisk > 30) score -= 20;
    
    // High-risk assets concentration
    const highRiskAssets = options + crypto;
    const highRiskPercentage = (highRiskAssets / (stocks + options + crypto)) * 100;
    if (highRiskPercentage <= 10) score += 15;
    else if (highRiskPercentage <= 20) score += 5;
    else if (highRiskPercentage > 30) score -= 15;
    
    return Math.max(0, Math.min(100, score));
  };

  const calculateLeverageScore = (leveragedPositions, marginUsed, portfolioValue, maxLeverage) => {
    let score = 50;
    
    const leverageRatio = leveragedPositions / portfolioValue;
    const marginRatio = marginUsed / portfolioValue;
    
    if (leverageRatio === 0 && marginRatio === 0) score += 30;
    else if (leverageRatio <= 0.1 && marginRatio <= 0.1) score += 15;
    else if (leverageRatio > maxLeverage) score -= 30;
    else if (leverageRatio > maxLeverage * 0.5) score -= 15;
    
    if (marginRatio > 0.5) score -= 20;
    else if (marginRatio > 0.25) score -= 10;
    
    return Math.max(0, Math.min(100, score));
  };

  const calculateMarketScore = (portfolioBeta, marketVolatility, vixLevel) => {
    let score = 50;
    
    // Beta adjustment
    if (portfolioBeta <= 0.8) score += 15;
    else if (portfolioBeta <= 1.0) score += 5;
    else if (portfolioBeta > 1.5) score -= 15;
    
    // Market volatility
    const vix = parseFloat(vixLevel) || 20;
    if (vix <= 15) score += 10;
    else if (vix <= 20) score += 5;
    else if (vix > 30) score -= 15;
    
    // Market condition
    if (marketVolatility === "Low") score += 10;
    else if (marketVolatility === "High") score -= 10;
    
    return Math.max(0, Math.min(100, score));
  };

  const calculateDiversificationScore = (stocks, options, crypto, other, cash) => {
    let score = 50;
    const total = stocks + options + crypto + other + cash;
    
    if (total === 0) return 50;
    
    // Calculate concentration in largest position
    const positions = [stocks, options, crypto, other, cash];
    const maxPosition = Math.max(...positions);
    const concentration = (maxPosition / total) * 100;
    
    if (concentration <= 20) score += 25;
    else if (concentration <= 35) score += 10;
    else if (concentration > 50) score -= 20;
    
    // Cash allocation
    const cashPercentage = (cash / total) * 100;
    if (cashPercentage >= 10 && cashPercentage <= 30) score += 15;
    else if (cashPercentage < 5) score -= 10;
    
    return Math.max(0, Math.min(100, score));
  };

  const calculateTimeHorizonScore = (timeHorizon, riskAppetite) => {
    let score = 50;
    
    // Time horizon alignment with risk appetite
    if (timeHorizon >= 10 && riskAppetite === "aggressive") score += 25;
    else if (timeHorizon >= 5 && riskAppetite === "moderate") score += 15;
    else if (timeHorizon <= 2 && riskAppetite === "conservative") score += 10;
    else if (timeHorizon <= 1 && riskAppetite === "aggressive") score -= 20;
    
    return Math.max(0, Math.min(100, score));
  };

  const calculateRiskCapacity = (portfolioValue, monthlyIncome, monthlyExpenses, emergencyFund, timeHorizon) => {
    const monthlySavings = monthlyIncome - monthlyExpenses;
    const emergencyMonths = emergencyFund / monthlyExpenses;
    
    return {
      monthlySavings,
      emergencyMonths,
      savingsRate: monthlyIncome > 0 ? (monthlySavings / monthlyIncome) * 100 : 0,
      recoveryCapacity: monthlySavings > 0 ? (portfolioValue * 0.2) / monthlySavings : 0, // Months to recover 20% loss
      timeHorizonAlignment: timeHorizon >= 5 ? "Good" : timeHorizon >= 3 ? "Moderate" : "Poor"
    };
  };

  const calculateStressTests = (portfolioValue, stocks, options, crypto, other, beta, marketCrash, volSpike, blackSwan, correlationBreakdown) => {
    const baseMarketImpact = stocks * beta * marketCrash;
    const optionsImpact = options * volSpike * 2; // Options more sensitive to volatility
    const cryptoImpact = crypto * blackSwan * 1.5; // Crypto more sensitive to black swan
    const otherImpact = other * marketCrash * correlationBreakdown;
    
    const totalImpact = baseMarketImpact + optionsImpact + cryptoImpact + otherImpact;
    const impactPercentage = (totalImpact / portfolioValue) * 100;
    
    return {
      marketCrash: {
        loss: baseMarketImpact,
        percentage: (baseMarketImpact / portfolioValue) * 100
      },
      volatilitySpike: {
        loss: optionsImpact,
        percentage: (optionsImpact / portfolioValue) * 100
      },
      blackSwan: {
        loss: cryptoImpact,
        percentage: (cryptoImpact / portfolioValue) * 100
      },
      correlationBreakdown: {
        loss: otherImpact,
        percentage: (otherImpact / portfolioValue) * 100
      },
      combined: {
        loss: totalImpact,
        percentage: impactPercentage
      }
    };
  };

  const calculateDetailedStressTests = (analysis, stressTests) => {
    const { portfolio, metrics } = analysis;
    
    const detailedTests = {
      mildRecession: {
        scenario: "Mild Recession (-15%)",
        loss: portfolio.totalValue * 0.15,
        probability: "High",
        impact: "Moderate",
        recovery: Math.min(24, metrics.monthlySavings > 0 ? (portfolio.totalValue * 0.15) / metrics.monthlySavings : 99)
      },
      severeBearMarket: {
        scenario: "Severe Bear Market (-30%)",
        loss: portfolio.totalValue * 0.30,
        probability: "Medium",
        impact: "High",
        recovery: Math.min(60, metrics.monthlySavings > 0 ? (portfolio.totalValue * 0.30) / metrics.monthlySavings : 99)
      },
      marketCrash: {
        scenario: "Market Crash (-40%)",
        loss: portfolio.totalValue * 0.40,
        probability: "Low",
        impact: "Severe",
        recovery: Math.min(99, metrics.monthlySavings > 0 ? (portfolio.totalValue * 0.40) / metrics.monthlySavings : 99)
      },
      volatilitySpike: {
        scenario: "Volatility Spike (VIX 40+)",
        loss: stressTests.volatilitySpike.loss,
        probability: "Medium",
        impact: stressTests.volatilitySpike.percentage > 10 ? "High" : "Moderate",
        recovery: Math.min(36, metrics.monthlySavings > 0 ? stressTests.volatilitySpike.loss / metrics.monthlySavings : 99)
      }
    };
    
    setStressTestResults(detailedTests);
  };

  const getExposureLevel = (exposureScore) => {
    if (exposureScore >= 80) return { 
      level: "Very Low", 
      color: "#28A745", 
      description: "Well-protected portfolio",
      recommendation: "Consider increasing risk for better returns"
    };
    if (exposureScore >= 70) return { 
      level: "Low", 
      color: "#2B8C3A", 
      description: "Conservative exposure",
      recommendation: "Maintain current risk level"
    };
    if (exposureScore >= 60) return { 
      level: "Moderate", 
      color: "#FFC107", 
      description: "Balanced risk exposure",
      recommendation: "Monitor positions regularly"
    };
    if (exposureScore >= 50) return { 
      level: "Elevated", 
      color: "#FF6B6B", 
      description: "Higher than optimal risk",
      recommendation: "Consider reducing concentrated positions"
    };
    return { 
      level: "High", 
      color: "#E53E3E", 
      description: "Dangerous risk levels",
      recommendation: "Immediate risk reduction required"
    };
  };

  const assessRiskAppetiteMatch = (exposureScore, riskAppetite) => {
    const appetiteScores = {
      conservative: { min: 70, max: 100 },
      moderate: { min: 60, max: 80 },
      aggressive: { min: 40, max: 70 }
    };
    
    const range = appetiteScores[riskAppetite] || appetiteScores.moderate;
    
    if (exposureScore >= range.min && exposureScore <= range.max) {
      return { match: "Aligned", color: "#28A745", description: "Risk exposure matches your appetite" };
    } else if (exposureScore > range.max) {
      return { match: "Too Conservative", color: "#FFC107", description: "Consider increasing risk for better returns" };
    } else {
      return { match: "Too Aggressive", color: "#E53E3E", description: "Reduce risk to match your comfort level" };
    }
  };

  const generateRecommendations = (analysis) => {
    const recommendations = [];
    const { riskScores, composition, capacity, exposure } = analysis;

    if (riskScores.liquidity < 60) {
      recommendations.push({
        type: "warning",
        message: "Low liquidity buffer",
        action: `Increase emergency fund to cover ${Math.max(6, Math.ceil(capacity.emergencyMonths) + 1)} months of expenses`
      });
    }

    if (riskScores.concentration < 60) {
      recommendations.push({
        type: "danger",
        message: "High concentration risk",
        action: "Diversify concentrated positions and reduce single-stock exposure"
      });
    }

    if (riskScores.leverage < 60) {
      recommendations.push({
        type: "warning",
        message: "Elevated leverage exposure",
        action: "Reduce margin usage and leveraged positions"
      });
    }

    if (composition.crypto > 10) {
      recommendations.push({
        type: "info",
        message: "High cryptocurrency allocation",
        action: "Consider reducing crypto exposure to below 10% of portfolio"
      });
    }

    if (capacity.savingsRate < 10) {
      recommendations.push({
        type: "warning",
        message: "Low savings rate",
        action: "Increase monthly savings to build better risk capacity"
      });
    }

    if (exposure.level === "High" || exposure.level === "Elevated") {
      recommendations.push({
        type: "danger",
        message: "Overall risk exposure too high",
        action: "Implement comprehensive risk reduction strategy"
      });
    }

    if (recommendations.length === 0) {
      recommendations.push({
        type: "success",
        message: "Risk exposure well managed",
        action: "Continue current risk management practices"
      });
    }

    return recommendations;
  };

  const resetCalculator = () => {
    setInputs({
      totalPortfolioValue: "100000",
      liquidAssets: "25000",
      monthlyIncome: "5000",
      monthlyExpenses: "3500",
      emergencyFund: "15000",
      stockPositions: "45000",
      optionPositions: "5000",
      cryptoPositions: "5000",
      otherPositions: "5000",
      cashPosition: "30000",
      leveragedPositions: "0",
      marginUsed: "0",
      portfolioBeta: "1.2",
      concentrationRisk: "15",
      marketVolatility: "Normal",
      vixLevel: "18.5",
      economicOutlook: "Stable",
      sectorCorrelation: "0.7",
      maxDrawdownTolerance: "15",
      maxLeverage: "2",
      riskAppetite: "moderate",
      timeHorizon: "5",
      marketCrashScenario: "20",
      volatilitySpike: "50",
      blackSwanEvent: "35",
      correlationBreakdown: "0.9"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!exposureAnalysis) return;
    
    const csvContent = [
      "Metric,Value,Score",
      `Total Portfolio Value,$${exposureAnalysis.portfolio.totalValue.toFixed(2)},N/A`,
      `Overall Risk Exposure,${exposureAnalysis.exposure.level},${exposureAnalysis.riskScores.overall.toFixed(1)}`,
      `Liquidity Score,N/A,${exposureAnalysis.riskScores.liquidity.toFixed(1)}`,
      `Concentration Score,N/A,${exposureAnalysis.riskScores.concentration.toFixed(1)}`,
      `Leverage Score,N/A,${exposureAnalysis.riskScores.leverage.toFixed(1)}`,
      `Market Score,N/A,${exposureAnalysis.riskScores.market.toFixed(1)}`,
      `Diversification Score,N/A,${exposureAnalysis.riskScores.diversification.toFixed(1)}`,
      `Stock Allocation,${exposureAnalysis.composition.stocks.toFixed(1)}%,N/A`,
      `Options Allocation,${exposureAnalysis.composition.options.toFixed(1)}%,N/A`,
      `Crypto Allocation,${exposureAnalysis.composition.crypto.toFixed(1)}%,N/A`,
      `Cash Allocation,${exposureAnalysis.composition.cash.toFixed(1)}%,N/A`,
      `Emergency Fund Months,${exposureAnalysis.capacity.emergencyMonths.toFixed(1)},N/A`,
      `Monthly Savings Rate,${exposureAnalysis.capacity.savingsRate.toFixed(1)}%,N/A`,
      `Risk Appetite Match,${exposureAnalysis.assessment.match},N/A`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'risk-exposure-analysis.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Analysis exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!exposureAnalysis) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      analysis: exposureAnalysis
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Analysis saved to history", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { 
        riskAppetite: "conservative", 
        maxDrawdownTolerance: "10",
        maxLeverage: "1",
        concentrationRisk: "10"
      },
      moderate: { 
        riskAppetite: "moderate", 
        maxDrawdownTolerance: "15",
        maxLeverage: "2",
        concentrationRisk: "15"
      },
      aggressive: { 
        riskAppetite: "aggressive", 
        maxDrawdownTolerance: "25",
        maxLeverage: "3",
        concentrationRisk: "20"
      }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} risk profile applied`, "info");
  };

  const portfolioPreset = (type) => {
    const presets = {
      growth: { 
        stockPositions: "60000",
        optionPositions: "10000",
        cryptoPositions: "5000",
        cashPosition: "15000",
        portfolioBeta: "1.4"
      },
      balanced: { 
        stockPositions: "45000",
        optionPositions: "5000",
        cryptoPositions: "5000",
        cashPosition: "30000",
        portfolioBeta: "1.2"
      },
      income: { 
        stockPositions: "30000",
        optionPositions: "2000",
        cryptoPositions: "1000",
        cashPosition: "45000",
        portfolioBeta: "0.8"
      }
    };
    
    setInputs(prev => ({ ...prev, ...presets[type] }));
    addNotification(`${type} portfolio preset applied`, "info");
  };

  // Chart Data
  const exposureRadarData = exposureAnalysis ? {
    labels: ['Liquidity', 'Concentration', 'Leverage', 'Market', 'Diversification', 'Time Horizon'],
    datasets: [{
      label: 'Risk Exposure Scores',
      data: [
        exposureAnalysis.riskScores.liquidity,
        exposureAnalysis.riskScores.concentration,
        exposureAnalysis.riskScores.leverage,
        exposureAnalysis.riskScores.market,
        exposureAnalysis.riskScores.diversification,
        exposureAnalysis.riskScores.timeHorizon
      ],
      backgroundColor: 'rgba(25, 105, 44, 0.2)',
      borderColor: '#19692C',
      pointBackgroundColor: '#19692C',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: '#19692C'
    }]
  } : null;

  const portfolioAllocationData = exposureAnalysis ? {
    labels: ['Stocks', 'Options', 'Cryptocurrency', 'Other Assets', 'Cash'],
    datasets: [{
      data: [
        exposureAnalysis.composition.stocks,
        exposureAnalysis.composition.options,
        exposureAnalysis.composition.crypto,
        exposureAnalysis.composition.other,
        exposureAnalysis.composition.cash
      ],
      backgroundColor: [
        "#19692C",
        "#2B8C3A",
        "#FFC107",
        "#FF6B6B",
        "#E8F5EB"
      ],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const stressTestData = stressTestResults ? {
    labels: Object.values(stressTestResults).map(test => test.scenario),
    datasets: [{
      label: 'Potential Loss ($)',
      data: Object.values(stressTestResults).map(test => test.loss),
      backgroundColor: [
        'rgba(255, 193, 7, 0.6)',
        'rgba(255, 107, 107, 0.6)',
        'rgba(229, 62, 62, 0.6)',
        'rgba(255, 193, 7, 0.8)'
      ],
      borderColor: '#19692C',
      borderWidth: 2
    }]
  } : null;

  const exposureLevel = exposureAnalysis ? exposureAnalysis.exposure : null;
  const riskAppetiteMatch = exposureAnalysis ? exposureAnalysis.assessment : null;
  const recommendations = exposureAnalysis ? generateRecommendations(exposureAnalysis) : [];

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Risk Exposure Meter</h1>
        </div>
        <p>Comprehensive portfolio risk assessment and exposure analysis across multiple dimensions</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "meter" ? "active" : ""}`}
                onClick={() => setActiveTab("meter")}
              >
                <i className="fas fa-tachometer-alt"></i>
                {isMobile ? 'Meter' : 'Exposure Meter'}
              </button>
              <button 
                className={`tab ${activeTab === "stress" ? "active" : ""}`}
                onClick={() => setActiveTab("stress")}
              >
                <i className="fas fa-bolt"></i>
                {isMobile ? 'Stress Tests' : 'Stress Testing'}
              </button>
              <button 
                className={`tab ${activeTab === "composition" ? "active" : ""}`}
                onClick={() => setActiveTab("composition")}
              >
                <i className="fas fa-chart-pie"></i>
                {isMobile ? 'Composition' : 'Portfolio Analysis'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Risk Profile Presets */}
          <div className="presets-section">
            <h4>Risk Profile:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Analysis Mode Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${analysisMode === "comprehensive" ? "active" : ""}`}
                onClick={() => setAnalysisMode("comprehensive")}
              >
                <i className="fas fa-chart-bar"></i>
                Comprehensive
              </button>
              <button 
                className={`mode-btn ${analysisMode === "liquidity" ? "active" : ""}`}
                onClick={() => setAnalysisMode("liquidity")}
              >
                <i className="fas fa-money-bill-wave"></i>
                Liquidity Focus
              </button>
              <button 
                className={`mode-btn ${analysisMode === "market" ? "active" : ""}`}
                onClick={() => setAnalysisMode("market")}
              >
                <i className="fas fa-chart-line"></i>
                Market Risk
              </button>
            </div>
            <div className="mode-description">
              {analysisMode === "comprehensive" 
                ? "Full risk assessment across all portfolio dimensions"
                : analysisMode === "liquidity"
                ? "Focus on cash flow, emergency funds, and liquidity risk"
                : "Emphasis on market exposure, beta, and volatility risk"
              }
            </div>
          </div>

          {/* Portfolio Presets */}
          <div className="presets-section">
            <h4>Portfolio Type:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => portfolioPreset('income')}>
                Income
              </button>
              <button className="preset-btn moderate" onClick={() => portfolioPreset('balanced')}>
                Balanced
              </button>
              <button className="preset-btn aggressive" onClick={() => portfolioPreset('growth')}>
                Growth
              </button>
            </div>
          </div>

          {/* Exposure Meter Tab */}
          {activeTab === "meter" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-wallet"></i>
                    Portfolio Overview
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Total Portfolio Value</label>
                      <input 
                        type="number" 
                        name="totalPortfolioValue" 
                        value={inputs.totalPortfolioValue} 
                        onChange={handleChange}
                        placeholder="100000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-money-bill-wave"></i> Liquid Assets</label>
                      <input 
                        type="number" 
                        name="liquidAssets" 
                        value={inputs.liquidAssets} 
                        onChange={handleChange}
                        placeholder="25000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-up"></i> Monthly Income</label>
                      <input 
                        type="number" 
                        name="monthlyIncome" 
                        value={inputs.monthlyIncome} 
                        onChange={handleChange}
                        placeholder="5000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-down"></i> Monthly Expenses</label>
                      <input 
                        type="number" 
                        name="monthlyExpenses" 
                        value={inputs.monthlyExpenses} 
                        onChange={handleChange}
                        placeholder="3500"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-shield-alt"></i>
                    Risk Parameters
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-umbrella"></i> Emergency Fund</label>
                      <input 
                        type="number" 
                        name="emergencyFund" 
                        value={inputs.emergencyFund} 
                        onChange={handleChange}
                        placeholder="15000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-down"></i> Max Drawdown Tolerance (%)</label>
                      <input 
                        type="number" 
                        name="maxDrawdownTolerance" 
                        value={inputs.maxDrawdownTolerance} 
                        onChange={handleChange}
                        placeholder="15"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-rocket"></i> Max Leverage</label>
                      <input 
                        type="number" 
                        name="maxLeverage" 
                        value={inputs.maxLeverage} 
                        onChange={handleChange}
                        placeholder="2"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-user-shield"></i> Risk Appetite</label>
                      <select name="riskAppetite" value={inputs.riskAppetite} onChange={handleChange}>
                        <option value="conservative">Conservative</option>
                        <option value="moderate">Moderate</option>
                        <option value="aggressive">Aggressive</option>
                      </select>
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Market Conditions
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-wave-square"></i> Market Volatility</label>
                      <select name="marketVolatility" value={inputs.marketVolatility} onChange={handleChange}>
                        <option value="Low">Low</option>
                        <option value="Normal">Normal</option>
                        <option value="High">High</option>
                        <option value="Extreme">Extreme</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-gauge-high"></i> VIX Level</label>
                      <input 
                        type="number" 
                        name="vixLevel" 
                        value={inputs.vixLevel} 
                        onChange={handleChange}
                        placeholder="18.5"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-globe"></i> Economic Outlook</label>
                      <select name="economicOutlook" value={inputs.economicOutlook} onChange={handleChange}>
                        <option value="Recession">Recession</option>
                        <option value="Stable">Stable</option>
                        <option value="Growth">Growth</option>
                        <option value="Boom">Boom</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-clock"></i> Time Horizon (Years)</label>
                      <input 
                        type="number" 
                        name="timeHorizon" 
                        value={inputs.timeHorizon} 
                        onChange={handleChange}
                        placeholder="5"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save Analysis
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Stress Testing Tab */}
          {activeTab === "stress" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-bolt"></i>
                  Stress Test Scenarios
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-line-down"></i> Market Crash Scenario (%)</label>
                    <input 
                      type="number" 
                      name="marketCrashScenario" 
                      value={inputs.marketCrashScenario} 
                      onChange={handleChange}
                      placeholder="20"
                      step="0.1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-wave-square"></i> Volatility Spike (%)</label>
                    <input 
                      type="number" 
                      name="volatilitySpike" 
                      value={inputs.volatilitySpike} 
                      onChange={handleChange}
                      placeholder="50"
                      step="0.1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-skull-crossbones"></i> Black Swan Event (%)</label>
                    <input 
                      type="number" 
                      name="blackSwanEvent" 
                      value={inputs.blackSwanEvent} 
                      onChange={handleChange}
                      placeholder="35"
                      step="0.1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-link-slash"></i> Correlation Breakdown</label>
                    <input 
                      type="number" 
                      name="correlationBreakdown" 
                      value={inputs.correlationBreakdown} 
                      onChange={handleChange}
                      placeholder="0.9"
                      step="0.1"
                    />
                  </div>
                </div>

                <h3>
                  <i className="fas fa-layer-group"></i>
                  Current Positions
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-line"></i> Stock Positions</label>
                    <input 
                      type="number" 
                      name="stockPositions" 
                      value={inputs.stockPositions} 
                      onChange={handleChange}
                      placeholder="45000"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-chess-board"></i> Option Positions</label>
                    <input 
                      type="number" 
                      name="optionPositions" 
                      value={inputs.optionPositions} 
                      onChange={handleChange}
                      placeholder="5000"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-coins"></i> Crypto Positions</label>
                    <input 
                      type="number" 
                      name="cryptoPositions" 
                      value={inputs.cryptoPositions} 
                      onChange={handleChange}
                      placeholder="5000"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-cube"></i> Other Positions</label>
                    <input 
                      type="number" 
                      name="otherPositions" 
                      value={inputs.otherPositions} 
                      onChange={handleChange}
                      placeholder="5000"
                    />
                  </div>
                </div>

                <h3>
                  <i className="fas fa-cogs"></i>
                  Advanced Risk Factors
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-rocket"></i> Leveraged Positions</label>
                    <input 
                      type="number" 
                      name="leveragedPositions" 
                      value={inputs.leveragedPositions} 
                      onChange={handleChange}
                      placeholder="0"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-percentage"></i> Margin Used</label>
                    <input 
                      type="number" 
                      name="marginUsed" 
                      value={inputs.marginUsed} 
                      onChange={handleChange}
                      placeholder="0"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-balance-scale"></i> Portfolio Beta</label>
                    <input 
                      type="number" 
                      name="portfolioBeta" 
                      value={inputs.portfolioBeta} 
                      onChange={handleChange}
                      placeholder="1.2"
                      step="0.1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-bullseye"></i> Concentration Risk (%)</label>
                    <input 
                      type="number" 
                      name="concentrationRisk" 
                      value={inputs.concentrationRisk} 
                      onChange={handleChange}
                      placeholder="15"
                      step="0.1"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Exposure Analysis History
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No analyses yet. Start assessing to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className="history-profit" style={{ color: calc.analysis.exposure.color }}>
                          <i className="fas fa-tachometer-alt"></i>
                          {calc.analysis.exposure.level}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-chart-line"></i> Score: {calc.analysis.riskScores.overall.toFixed(1)}</span>
                        <span><i className="fas fa-balance-scale"></i> Match: {calc.analysis.assessment.match}</span>
                        <span><i className="fas fa-dollar-sign"></i> Portfolio: ${calc.analysis.portfolio.totalValue.toFixed(0)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "meter" || activeTab === "stress" || activeTab === "composition") && (
          <div className="right-panel">
            {activeTab === "meter" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-tachometer-alt"></i>
                    Risk Exposure Meter
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {exposureAnalysis ? (
                  <>
                    {/* Exposure Summary */}
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value" style={{ color: '#FFFFFF' }}>
                          {exposureAnalysis.riskScores.overall.toFixed(1)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-tachometer-alt"></i>
                          Exposure Score
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value" style={{ color: exposureLevel.color }}>
                          {exposureLevel.level}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-flag"></i>
                          Risk Level
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value" style={{ color: riskAppetiteMatch.color }}>
                          {riskAppetiteMatch.match}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-user-check"></i>
                          Appetite Match
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {exposureAnalysis.capacity.emergencyMonths.toFixed(1)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-umbrella"></i>
                          Emergency Months
                        </div>
                      </div>
                    </div>

                    {/* Risk Exposure Meter */}
                    <div className="risk-summary">
                      <h4>Risk Exposure: <span style={{ color: exposureLevel.color }}>{exposureLevel.level} - {exposureLevel.description}</span></h4>
                      <div className="risk-meter">
                        <div className="meter-bar">
                          <div 
                            className="meter-fill"
                            style={{ 
                              width: `${exposureAnalysis.riskScores.overall}%`,
                              backgroundColor: exposureLevel.color
                            }}
                          ></div>
                        </div>
                        <div className="meter-value" style={{ color: exposureLevel.color }}>
                          {exposureLevel.recommendation}
                        </div>
                      </div>
                    </div>

                    {/* Risk Appetite Alignment */}
                    <div className="risk-summary">
                      <h4>Risk Appetite Alignment: <span style={{ color: riskAppetiteMatch.color }}>{riskAppetiteMatch.match}</span></h4>
                      <div className="risk-metrics">
                        <div className="risk-metric">
                          <span className="metric-label">Your Risk Appetite:</span>
                          <span className="metric-value">{inputs.riskAppetite.charAt(0).toUpperCase() + inputs.riskAppetite.slice(1)}</span>
                        </div>
                        <div className="risk-metric">
                          <span className="metric-label">Actual Exposure:</span>
                          <span className="metric-value" style={{ color: exposureLevel.color }}>{exposureLevel.level}</span>
                        </div>
                        <div className="risk-metric">
                          <span className="metric-label">Assessment:</span>
                          <span className="metric-value" style={{ color: riskAppetiteMatch.color }}>{riskAppetiteMatch.description}</span>
                        </div>
                      </div>
                    </div>

                    {/* Detailed Risk Scores */}
                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-money-bill-wave"></i> Liquidity Risk:</span>
                        <span style={{ color: exposureAnalysis.riskScores.liquidity >= 70 ? '#28A745' : exposureAnalysis.riskScores.liquidity >= 50 ? '#FFC107' : '#E53E3E' }}>
                          {exposureAnalysis.riskScores.liquidity.toFixed(1)}/100
                        </span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-bullseye"></i> Concentration Risk:</span>
                        <span style={{ color: exposureAnalysis.riskScores.concentration >= 70 ? '#28A745' : exposureAnalysis.riskScores.concentration >= 50 ? '#FFC107' : '#E53E3E' }}>
                          {exposureAnalysis.riskScores.concentration.toFixed(1)}/100
                        </span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-rocket"></i> Leverage Risk:</span>
                        <span style={{ color: exposureAnalysis.riskScores.leverage >= 70 ? '#28A745' : exposureAnalysis.riskScores.leverage >= 50 ? '#FFC107' : '#E53E3E' }}>
                          {exposureAnalysis.riskScores.leverage.toFixed(1)}/100
                        </span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Market Risk:</span>
                        <span style={{ color: exposureAnalysis.riskScores.market >= 70 ? '#28A745' : exposureAnalysis.riskScores.market >= 50 ? '#FFC107' : '#E53E3E' }}>
                          {exposureAnalysis.riskScores.market.toFixed(1)}/100
                        </span>
                      </div>
                    </div>

                    {/* Risk Radar Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-bullseye"></i>
                        Multi-Dimensional Risk Exposure
                      </h3>
                      <Radar 
                        data={exposureRadarData}
                        options={{
                          responsive: true,
                          scales: {
                            r: {
                              beginAtZero: true,
                              max: 100,
                              ticks: {
                                stepSize: 20
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Recommendations */}
                    <div className="risk-summary">
                      <h4>Risk Management Recommendations</h4>
                      <div className="scenario-list">
                        {recommendations.map((rec, index) => (
                          <div key={index} className="scenario-item">
                            <div className="scenario-name">
                              <i className={`fas fa-${rec.type === 'success' ? 'check' : rec.type === 'warning' ? 'exclamation-triangle' : 'exclamation-circle'} fa-xs`}></i>
                              {rec.message}
                            </div>
                            <div className="scenario-outcome">{rec.action}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-tachometer-alt fa-3x"></i>
                    <p>Enter your portfolio details to assess risk exposure</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "stress" && stressTestResults && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-bolt"></i>
                  Stress Test Results
                </h3>

                {/* Stress Test Summary */}
                <div className="projection-summary">
                  {Object.entries(stressTestResults).map(([key, test]) => (
                    <div key={key} className="summary-card">
                      <div className="summary-value" style={{ 
                        color: test.impact === 'Severe' ? '#E53E3E' : 
                               test.impact === 'High' ? '#FF6B6B' : 
                               test.impact === 'Moderate' ? '#FFC107' : '#28A745'
                      }}>
                        ${test.loss.toLocaleString(undefined, {maximumFractionDigits: 0})}
                      </div>
                      <div className="summary-label">
                        <i className="fas fa-bolt"></i>
                        {test.scenario}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Stress Test Chart */}
                <div className="chart-container">
                  <h3>
                    <i className="fas fa-chart-bar"></i>
                    Stress Test Scenarios
                  </h3>
                  <Bar 
                    data={stressTestData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: false
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: function(value) {
                              return '$' + value.toLocaleString(undefined, {maximumFractionDigits: 0});
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>

                {/* Detailed Stress Test Analysis */}
                <div className="risk-summary">
                  <h4>Scenario Analysis</h4>
                  <div className="scenario-list">
                    {Object.entries(stressTestResults).map(([key, test]) => (
                      <div key={key} className="scenario-item">
                        <div className="scenario-name">{test.scenario}</div>
                        <div className="scenario-stats">
                          <div className="breakdown-item">
                            <span>Probability:</span>
                            <span style={{ 
                              color: test.probability === 'High' ? '#E53E3E' : 
                                     test.probability === 'Medium' ? '#FFC107' : '#28A745'
                            }}>
                              {test.probability}
                            </span>
                          </div>
                          <div className="breakdown-item">
                            <span>Impact:</span>
                            <span style={{ 
                              color: test.impact === 'Severe' ? '#E53E3E' : 
                                     test.impact === 'High' ? '#FF6B6B' : 
                                     test.impact === 'Moderate' ? '#FFC107' : '#28A745'
                            }}>
                              {test.impact}
                            </span>
                          </div>
                          <div className="breakdown-item">
                            <span>Recovery (Months):</span>
                            <span>{test.recovery.toFixed(1)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "composition" && exposureAnalysis && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-chart-pie"></i>
                  Portfolio Composition Analysis
                </h3>
                
                {/* Portfolio Allocation */}
                <div className="chart-container">
                  <h3>
                    <i className="fas fa-chart-pie"></i>
                    Portfolio Allocation
                  </h3>
                  <Doughnut 
                    data={portfolioAllocationData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          position: isMobile ? 'top' : 'right'
                        }
                      }
                    }}
                  />
                </div>

                {/* Portfolio Metrics */}
                <div className="scenario-comparison">
                  <div className="scenario-item">
                    <div className="scenario-name">Portfolio Metrics</div>
                    <div className="scenario-stats">
                      <div className="breakdown-item">
                        <span>Total Portfolio Value:</span>
                        <span>${exposureAnalysis.portfolio.totalValue.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Invested Amount:</span>
                        <span>${exposureAnalysis.portfolio.investedValue.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Cash Allocation:</span>
                        <span>{exposureAnalysis.portfolio.cashPercentage.toFixed(1)}%</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Portfolio Beta:</span>
                        <span>{exposureAnalysis.metrics.beta.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="scenario-item">
                    <div className="scenario-name">Risk Capacity</div>
                    <div className="scenario-stats">
                      <div className="breakdown-item">
                        <span>Monthly Savings:</span>
                        <span style={{ color: exposureAnalysis.capacity.monthlySavings >= 0 ? '#28A745' : '#E53E3E' }}>
                          ${exposureAnalysis.capacity.monthlySavings.toFixed(0)}
                        </span>
                      </div>
                      <div className="breakdown-item">
                        <span>Savings Rate:</span>
                        <span style={{ 
                          color: exposureAnalysis.capacity.savingsRate >= 20 ? '#28A745' : 
                                 exposureAnalysis.capacity.savingsRate >= 10 ? '#FFC107' : '#E53E3E'
                        }}>
                          {exposureAnalysis.capacity.savingsRate.toFixed(1)}%
                        </span>
                      </div>
                      <div className="breakdown-item">
                        <span>Time Horizon Alignment:</span>
                        <span style={{ 
                          color: exposureAnalysis.capacity.timeHorizonAlignment === 'Good' ? '#28A745' : 
                                 exposureAnalysis.capacity.timeHorizonAlignment === 'Moderate' ? '#FFC107' : '#E53E3E'
                        }}>
                          {exposureAnalysis.capacity.timeHorizonAlignment}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Asset Class Analysis */}
                <div className="risk-summary">
                  <h4>Asset Class Risk Assessment</h4>
                  <div className="risk-metrics">
                    <div className="risk-metric">
                      <span className="metric-label">Stocks ({exposureAnalysis.composition.stocks.toFixed(1)}%):</span>
                      <span className="metric-value" style={{ 
                        color: exposureAnalysis.composition.stocks <= 60 ? '#28A745' : 
                               exposureAnalysis.composition.stocks <= 80 ? '#FFC107' : '#E53E3E'
                      }}>
                        {exposureAnalysis.composition.stocks <= 60 ? 'Appropriate' : 
                         exposureAnalysis.composition.stocks <= 80 ? 'Elevated' : 'High'}
                      </span>
                    </div>
                    <div className="risk-metric">
                      <span className="metric-label">Options ({exposureAnalysis.composition.options.toFixed(1)}%):</span>
                      <span className="metric-value" style={{ 
                        color: exposureAnalysis.composition.options <= 5 ? '#28A745' : 
                               exposureAnalysis.composition.options <= 10 ? '#FFC107' : '#E53E3E'
                      }}>
                        {exposureAnalysis.composition.options <= 5 ? 'Conservative' : 
                         exposureAnalysis.composition.options <= 10 ? 'Moderate' : 'Aggressive'}
                      </span>
                    </div>
                    <div className="risk-metric">
                      <span className="metric-label">Crypto ({exposureAnalysis.composition.crypto.toFixed(1)}%):</span>
                      <span className="metric-value" style={{ 
                        color: exposureAnalysis.composition.crypto <= 5 ? '#28A745' : 
                               exposureAnalysis.composition.crypto <= 10 ? '#FFC107' : '#E53E3E'
                      }}>
                        {exposureAnalysis.composition.crypto <= 5 ? 'Speculative' : 
                         exposureAnalysis.composition.crypto <= 10 ? 'High Risk' : 'Very High Risk'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default RiskExposureMeterCalculator;